import { isWeb } from '@/constants/environment'
import { useEffect, useRef } from 'react'
import { View } from 'react-native'

interface CashCalculatorKeyboardOptions {
    disabled: boolean
    onDigit: (digit: string) => void
    onBackspace: () => void
}

const useCashCalculatorKeyboard = ({
    disabled,
    onDigit,
    onBackspace,
}: CashCalculatorKeyboardOptions) => {
    const containerRef = useRef<View>(null)

    useEffect(() => {
        if (!isWeb) return

        const container = containerRef.current as unknown as HTMLElement | null
        if (!container) return

        const previousFocus = document.activeElement
        container.tabIndex = -1
        container.focus({ preventScroll: true })

        return () => {
            if (
                previousFocus instanceof HTMLElement &&
                previousFocus.isConnected &&
                (container.contains(document.activeElement) ||
                    document.activeElement === document.body)
            ) {
                previousFocus.focus({ preventScroll: true })
            }
        }
    }, [])

    useEffect(() => {
        if (!isWeb) return

        const container = containerRef.current as unknown as HTMLElement | null
        if (!container) return

        const handleKeyDown = (event: KeyboardEvent) => {
            if (
                disabled ||
                event.defaultPrevented ||
                event.isComposing ||
                event.altKey ||
                event.ctrlKey ||
                event.metaKey ||
                (event.target instanceof HTMLElement &&
                    event.target.closest(
                        'input, textarea, select, [contenteditable]:not([contenteditable="false"])'
                    ))
            ) {
                return
            }

            const digit = /^[0-9]$/.test(event.key)
                ? event.key
                : ['.', ',', 'Decimal'].includes(event.key)
                  ? ','
                  : undefined
            if (!digit && event.key !== 'Backspace') return

            event.preventDefault()
            event.stopPropagation()
            if (digit) onDigit(digit)
            else onBackspace()
        }

        container.addEventListener('keydown', handleKeyDown)
        return () => container.removeEventListener('keydown', handleKeyDown)
    }, [disabled, onDigit, onBackspace])

    return containerRef
}

export default useCashCalculatorKeyboard
